/**
 * Reports — monthly statistics report builder.
 *
 * Generates a polished HTML report with summary cards and a detailed
 * file-by-file table for any selected month. Supports three export
 * options: browser print, Excel (.xlsx), and PDF download.
 *
 * Accessible to both Adviser and Assistant roles.
 */

import React, { useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, Printer, Download, FileSpreadsheet,
  CheckCircle2, XCircle, Loader,
} from 'lucide-react';
import { getFilesByMonth } from '../api/filesApi';
import { getSettings } from '../api/settingsApi';
import {
  formatDate, formatDateTime, formatMonthYearDisplay, getCurrentMonthYear,
} from '../utils/dateHelpers';
import { isSLABreached } from '../utils/slaCalculator';
import { calculateBonusStatus } from '../utils/bonusCalculator';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Generate dropdown options for the last 12 months. */
function generateMonthOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    options.push({ value, label: formatMonthYearDisplay(value) });
  }
  return options;
}

/** Milliseconds between two ISO date strings (a − b). Returns null if either is missing. */
function diffMs(a, b) {
  if (!a || !b) return null;
  return new Date(a).getTime() - new Date(b).getTime();
}

/** Average of non-null positive values. Returns null if none. */
function avgMs(values) {
  const valid = values.filter(v => v !== null && v > 0);
  if (valid.length === 0) return null;
  return valid.reduce((s, v) => s + v, 0) / valid.length;
}

/** Format a millisecond duration as "1.2d" or "5.3h". */
function formatDuration(ms) {
  if (ms === null || ms === undefined) return '—';
  const hours = ms / 3600000;
  if (hours >= 24) return `${(hours / 24).toFixed(1)}d`;
  return `${hours.toFixed(1)}h`;
}

/** Compute all summary statistics from the file array. */
function computeStats(files, settings) {
  const totalSubmitted = files.length;
  const totalCompleted = files.filter(f => f.completedAt).length;
  const rejectedCount = files.filter(f => f.rejectionReason).length;

  // SLA compliance — only count files that have progressed past submitted
  const filesWithProgress = files.filter(f => f.drawnAt);
  const slaBreachedCount = filesWithProgress.filter(f => isSLABreached(f)).length;
  const slaComplianceRate = filesWithProgress.length > 0
    ? ((filesWithProgress.length - slaBreachedCount) / filesWithProgress.length * 100).toFixed(1)
    : '100.0';

  const submissionToDrawn = avgMs(files.map(f => diffMs(f.drawnAt, f.submittedAt)));
  const drawnToAwaitingSig = avgMs(files.map(f => diffMs(f.sentToSignAt, f.drawnAt)));
  const awaitingSigToSigned = avgMs(files.map(f => diffMs(f.signedAt, f.sentToSignAt)));
  const signedToCompleted = avgMs(files.map(f => diffMs(f.completedAt, f.signedAt)));
  const endToEnd = avgMs(files.map(f => diffMs(f.completedAt, f.submittedAt)));

  const qualifyingCount = files.filter(f => f.qualifiesForBonus).length;
  const bonusStatus = calculateBonusStatus(qualifyingCount, settings || {});

  return {
    totalSubmitted, totalCompleted, slaComplianceRate,
    submissionToDrawn, drawnToAwaitingSig, awaitingSigToSigned,
    signedToCompleted, endToEnd,
    qualifyingCount, bonusStatus,
    rejectedCount,
  };
}

/** Per-file row data for the detail table. */
function fileRowData(f) {
  return {
    clientName: f.clientName,
    workCategory: f.workCategory || '—',
    submitted: formatDate(f.submittedAt),
    timeInInbox: formatDuration(diffMs(f.drawnAt, f.submittedAt)),
    processingTime: formatDuration(diffMs(f.sentToSignAt, f.drawnAt)),
    signingTime: formatDuration(diffMs(f.signedAt, f.sentToSignAt)),
    totalTime: formatDuration(diffMs(f.completedAt, f.submittedAt)),
    slaMet: f.drawnAt ? !isSLABreached(f) : null,
    bonusQualifying: f.qualifiesForBonus,
  };
}

const MONTH_OPTIONS = generateMonthOptions();

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Reports() {
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthYear());
  const [files, setFiles] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportGenerated, setReportGenerated] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null); // 'excel' | 'pdf' | null
  const reportRef = useRef(null);

  // ---- Generate report ----
  const handleGenerate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [filesData, settingsData] = await Promise.all([
        getFilesByMonth(selectedMonth),
        getSettings(),
      ]);
      // Test records (is_test = true, CLAUDE.md §33) never appear in
      // reports — excluded from the summary stats and the detail rows.
      const realFiles = filesData.filter((f) => f.isTest !== true);
      realFiles.sort((a, b) => new Date(a.submittedAt) - new Date(b.submittedAt));
      setFiles(realFiles);
      setSettings(settingsData);
      setGeneratedAt(new Date());
      setReportGenerated(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  // ---- Export: Print ----
  const handlePrint = () => window.print();

  // ---- Export: Excel ----
  const handleExcel = async () => {
    setExporting('excel');
    try {
      const XLSX = await import('xlsx');
      const stats = computeStats(files, settings);
      const monthLabel = formatMonthYearDisplay(selectedMonth);

      // Summary sheet
      const summaryData = [
        ['Monthly Stats Report', monthLabel],
        ['Generated', formatDateTime(generatedAt)],
        [],
        ['Metric', 'Value'],
        ['Total Files Submitted', stats.totalSubmitted],
        ['Total Files Completed', stats.totalCompleted],
        ['SLA Compliance Rate', `${stats.slaComplianceRate}%`],
        ['Avg Submission to Drawn', formatDuration(stats.submissionToDrawn)],
        ['Avg Drawn to Awaiting Sig', formatDuration(stats.drawnToAwaitingSig)],
        ['Avg Awaiting Sig to Signed', formatDuration(stats.awaitingSigToSigned)],
        ['Avg Signed to Completed', formatDuration(stats.signedToCompleted)],
        ['Avg End-to-End Time', formatDuration(stats.endToEnd)],
        ['Bonus Qualifying Files', stats.qualifyingCount],
        ['Bonus Milestone', stats.bonusStatus.currentTier?.label || 'None'],
        ['Files Rejected', stats.rejectedCount],
      ];

      // Detail sheet
      const detailHeaders = [
        '#', 'Submitted', 'Client Name', 'Work Category',
        'Time in Inbox', 'Processing Time', 'Signing Time',
        'Total Time', 'SLA Met', 'Bonus Qualifying',
      ];
      const detailRows = files.map((f, idx) => {
        const r = fileRowData(f);
        return [
          idx + 1, r.submitted, r.clientName, r.workCategory,
          r.timeInInbox, r.processingTime, r.signingTime, r.totalTime,
          r.slaMet === null ? '—' : r.slaMet ? 'Yes' : 'No',
          r.bonusQualifying ? 'Yes' : 'No',
        ];
      });

      const wb = XLSX.utils.book_new();
      const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
      const wsDetail = XLSX.utils.aoa_to_sheet([detailHeaders, ...detailRows]);
      XLSX.utils.book_append_sheet(wb, wsDetail, 'File Details');

      const safeName = monthLabel.replace(/\s+/g, '_');
      XLSX.writeFile(wb, `Inbox_Report_${safeName}.xlsx`);
    } catch (err) {
      console.error('Excel export failed:', err);
    } finally {
      setExporting(null);
    }
  };

  // ---- Export: PDF ----
  const handlePdf = async () => {
    if (!reportRef.current) return;
    setExporting('pdf');
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas'),
      ]);

      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20; // 10mm margin each side
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 10; // top margin

      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= (pageHeight - 20);

      while (heightLeft > 0) {
        position -= (pageHeight - 20);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= (pageHeight - 20);
      }

      const safeName = formatMonthYearDisplay(selectedMonth).replace(/\s+/g, '_');
      pdf.save(`Inbox_Report_${safeName}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(null);
    }
  };

  // ---- Derived data ----
  const stats = reportGenerated ? computeStats(files, settings) : null;

  const summaryCards = stats ? [
    { label: 'Files Submitted', value: stats.totalSubmitted, color: '#0F4C75' },
    { label: 'Files Completed', value: stats.totalCompleted, color: '#1B9AAA' },
    { label: 'SLA Compliance', value: `${stats.slaComplianceRate}%`, color: parseFloat(stats.slaComplianceRate) >= 80 ? '#2EC4B6' : parseFloat(stats.slaComplianceRate) >= 60 ? '#F4A261' : '#C0392B' },
    { label: 'Avg Submission → Drawn', value: formatDuration(stats.submissionToDrawn), color: '#6B7A8D' },
    { label: 'Avg Drawn → Awaiting Sig.', value: formatDuration(stats.drawnToAwaitingSig), color: '#6B7A8D' },
    { label: 'Avg Awaiting Sig. → Signed', value: formatDuration(stats.awaitingSigToSigned), color: '#6B7A8D' },
    { label: 'Avg Signed → Completed', value: formatDuration(stats.signedToCompleted), color: '#6B7A8D' },
    { label: 'Avg End-to-End Time', value: formatDuration(stats.endToEnd), color: '#0F4C75' },
    { label: 'Bonus Qualifying Files', value: `${stats.qualifyingCount}${stats.bonusStatus.currentTier ? ` — ${stats.bonusStatus.currentTier.emoji} ${stats.bonusStatus.currentTier.label}` : ''}`, color: '#F4A261' },
    { label: 'Files Rejected', value: stats.rejectedCount, color: stats.rejectedCount > 0 ? '#C0392B' : '#6B7A8D' },
  ] : [];

  // ---- Render ----
  return (
    <div style={styles.page}>
      {/* Page title */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        style={styles.titleRow}
      >
        <FileText size={22} color="#0F4C75" />
        <h2 style={styles.pageTitle}>Reports</h2>
      </motion.div>

      {/* Controls */}
      <div className="report-controls" style={styles.controlsBar}>
        <select
          value={selectedMonth}
          onChange={e => { setSelectedMonth(e.target.value); setReportGenerated(false); }}
          style={styles.select}
        >
          {MONTH_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button onClick={handleGenerate} disabled={loading} style={styles.generateBtn}>
          {loading ? <Loader size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> : <FileText size={16} />}
          <span>{loading ? 'Generating...' : 'Generate Report'}</span>
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Report output */}
      {reportGenerated && stats && (
        <>
          <div ref={reportRef} className="report-container" style={styles.reportWrapper}>
            {/* Report header */}
            <div style={styles.reportHeader}>
              <div style={styles.reportTitle}>
                Monthly Stats Report — {formatMonthYearDisplay(selectedMonth)}
              </div>
              <div style={styles.reportSubtitle}>
                Generated: {formatDateTime(generatedAt)}
              </div>
              <div style={{ ...styles.reportSubtitle, marginTop: '0.25rem' }}>
                Inbox PFP | Private &amp; Confidential
              </div>
            </div>

            {/* Section 1 — Summary Statistics */}
            <div style={styles.sectionTitle}>Summary Statistics</div>
            <div className="report-summary-grid" style={styles.summaryGrid}>
              {summaryCards.map((card, i) => (
                <div key={i} style={styles.statCard}>
                  <div style={styles.statLabel}>{card.label}</div>
                  <div style={{ ...styles.statValue, color: card.color }}>{card.value}</div>
                </div>
              ))}
            </div>

            {/* Section 2 — File Detail Table */}
            <div style={styles.sectionTitle}>File Details</div>
            <div className="report-detail-table" style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {[
                      '#', 'Submitted', 'Client Name', 'Work Category',
                      'Time in Inbox', 'Processing Time', 'Signing Time',
                      'Total Time', 'SLA Met', 'Bonus Qual.',
                    ].map(h => <th key={h} style={styles.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {files.length === 0 && (
                    <tr><td colSpan={10} style={{ ...styles.td, textAlign: 'center', color: '#6B7A8D' }}>No files for this month.</td></tr>
                  )}
                  {files.map((f, i) => {
                    const r = fileRowData(f);
                    const rowStyle = i % 2 === 0 ? styles.rowEven : styles.rowOdd;
                    return (
                      <tr key={f.id} style={rowStyle}>
                        <td style={{ ...styles.td, textAlign: 'center', color: '#6B7A8D' }}>{i + 1}</td>
                        <td style={styles.td}>{r.submitted}</td>
                        <td style={{ ...styles.td, fontWeight: 600 }}>{r.clientName}</td>
                        <td style={styles.td}>{r.workCategory}</td>
                        <td style={styles.td}>{r.timeInInbox}</td>
                        <td style={styles.td}>{r.processingTime}</td>
                        <td style={styles.td}>{r.signingTime}</td>
                        <td style={styles.td}>{r.totalTime}</td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {r.slaMet === null ? '—' : r.slaMet
                            ? <CheckCircle2 size={14} color="#2EC4B6" />
                            : <XCircle size={14} color="#C0392B" />
                          }
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {r.bonusQualifying
                            ? <CheckCircle2 size={14} color="#2EC4B6" />
                            : <XCircle size={14} color="#C0392B" />
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Export buttons */}
          <div className="report-export-buttons" style={styles.exportBar}>
            <button onClick={handlePrint} style={{ ...styles.exportBtn, ...styles.exportBtnPrimary }}>
              <Printer size={16} /> Print / Save as PDF
            </button>
            <button onClick={handleExcel} disabled={exporting === 'excel'} style={{ ...styles.exportBtn, ...styles.exportBtnAccent }}>
              {exporting === 'excel'
                ? <Loader size={16} style={{ animation: 'spin 0.8s linear infinite' }} />
                : <FileSpreadsheet size={16} />
              }
              Download Excel
            </button>
            <button onClick={handlePdf} disabled={exporting === 'pdf'} style={{ ...styles.exportBtn, ...styles.exportBtnSecondary }}>
              {exporting === 'pdf'
                ? <Loader size={16} style={{ animation: 'spin 0.8s linear infinite' }} />
                : <Download size={16} />
              }
              Download PDF
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = {
  page: {
    padding: '0.5rem 0',
    minHeight: '60vh',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1.25rem',
  },
  pageTitle: {
    margin: 0,
    fontSize: '1.3rem',
    fontWeight: 700,
    color: 'var(--cp-text)',
  },
  controlsBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1.5rem',
    padding: '1rem 1.25rem',
    background: 'var(--cp-surface)',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(15, 76, 117, 0.06)',
    border: '1px solid var(--cp-surface-alt)',
    flexWrap: 'wrap',
  },
  select: {
    padding: '0.55rem 0.85rem',
    border: '1.5px solid var(--cp-border)',
    borderRadius: '10px',
    fontSize: '0.9rem',
    color: 'var(--cp-text)',
    background: 'var(--cp-surface)',
    cursor: 'pointer',
    minWidth: '180px',
  },
  generateBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.55rem 1.1rem',
    background: 'var(--cp-primary)',
    color: 'var(--cp-surface)',
    border: 'none',
    borderRadius: '10px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  error: {
    padding: '0.75rem 1rem',
    background: '#FDF2F2',
    color: 'var(--cp-danger)',
    borderRadius: '10px',
    fontSize: '0.85rem',
    marginBottom: '1rem',
    border: '1px solid #F5C6CB',
  },

  // Report wrapper
  reportWrapper: {
    background: '#FFFFFF',
    borderRadius: '16px',
    boxShadow: '0 4px 16px rgba(15, 76, 117, 0.10)',
    overflow: 'hidden',
    marginBottom: '0.5rem',
  },
  reportHeader: {
    background: 'linear-gradient(135deg, #0F4C75, #1B9AAA)',
    padding: '1.75rem 2rem',
    color: '#FFFFFF',
  },
  reportTitle: {
    fontSize: '1.4rem',
    fontWeight: 700,
    letterSpacing: '-0.01em',
  },
  reportSubtitle: {
    fontSize: '0.85rem',
    opacity: 0.85,
    marginTop: '0.35rem',
  },

  // Section titles
  sectionTitle: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#0F4C75',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    padding: '1.25rem 1.5rem 0.5rem',
  },

  // Summary grid
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '0.85rem',
    padding: '0.5rem 1.5rem 1.25rem',
  },
  statCard: {
    background: '#FFFFFF',
    border: '1px solid #E8EFF5',
    borderRadius: '12px',
    padding: '1rem 1.15rem',
    textAlign: 'center',
    boxShadow: '0 1px 4px rgba(15, 76, 117, 0.04)',
  },
  statLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#6B7A8D',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '0.35rem',
  },
  statValue: {
    fontSize: '1.45rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },

  // Table
  tableContainer: {
    overflowX: 'auto',
    padding: '0 1.5rem 1.5rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.75rem',
  },
  th: {
    background: '#0F4C75',
    color: '#FFFFFF',
    padding: '0.6rem 0.5rem',
    textAlign: 'left',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    fontSize: '0.7rem',
    letterSpacing: '0.02em',
  },
  td: {
    padding: '0.5rem',
    borderBottom: '1px solid #E8EFF5',
    whiteSpace: 'nowrap',
    color: '#1A2B3C',
  },
  rowEven: { background: '#F7F9FC' },
  rowOdd: { background: '#FFFFFF' },

  // Export buttons
  exportBar: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1.25rem',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  exportBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.5rem 1rem',
    borderRadius: '10px',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: '1.5px solid',
  },
  exportBtnPrimary: {
    color: 'var(--cp-primary)',
    borderColor: 'var(--cp-primary)',
    background: 'rgba(15, 76, 117, 0.06)',
  },
  exportBtnAccent: {
    color: 'var(--cp-accent)',
    borderColor: 'var(--cp-accent)',
    background: 'rgba(244, 162, 97, 0.06)',
  },
  exportBtnSecondary: {
    color: 'var(--cp-secondary)',
    borderColor: 'var(--cp-secondary)',
    background: 'rgba(27, 154, 170, 0.06)',
  },
};
